import { Box, Stack } from "@mantine/core";
import {
  AnnouncementBanner,
  AnnouncementCorner,
  AnnouncementFullscreen,
  AnnouncementModal,
  applyTakeoverSuppression,
  groupByDisplayType,
  isPermanentlyDismissable,
} from "@nodus/announcements";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAnnouncements } from "@/hooks/use-announcements";
import { usePortalEvent, RECONNECT_EVENT } from "@/lib/portal-socket";
import { useAnnouncementDismissal } from "@/lib/announcements-dismissal";
import { AnnouncementAudioPlayers, AnnouncementSoundPanel } from "@/components/announcement-sound";
import { AnnouncementCornerCarousel } from "@/components/announcement-corner-carousel";
import { useGuideModal } from "@/hooks/use-guide-modal";
import { guideKeyFromLink } from "@/lib/guide-stepper";

/**
 * Renders all active announcements across the member portal per their
 * ``display_type``: banners stacked at the top (inline), corner cards in one
 * popup bottom-left (fixed), and one fullscreen takeover modal.
 *
 * Corner cards: the ordinary ones (events, posters) share ONE popup that pages
 * between them (``AnnouncementCornerCarousel``) so several never stack over
 * the page. Critical / emergency corner cards are not folded into it: each
 * stays on screen on its own, above the popup, as before.
 *
 * Dismiss rule: non-critical close permanently (localStorage); critical /
 * emergency / takeover close for the session only and re-show on reload —
 * they can never be permanently silenced. The header indicator keeps every
 * active announcement reachable regardless of dismissal.
 *
 * Sound: a visible announcement with an audio clip plays it on the kiosk's
 * schedule once the member has enabled alarm sounds in this browser, until
 * they mute it (see ``announcement-sound``). Dismissing a card stops its sound
 * too — the member has evidently seen it.
 */
export function AnnouncementsHost() {
  // Realtime: the first consumer of the portal WS layer. A push refetches
  // the query below, so the 30 s poll (kept as a safety net) becomes a
  // ~1 s update. ``reconnect`` catches up anything missed while offline.
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["announcements"] }),
    [queryClient],
  );
  usePortalEvent("announcements_changed", refetch);
  usePortalEvent(RECONNECT_EVENT, refetch);

  const announcements = useAnnouncements();
  // Shared with the header indicator so a dismissed announcement can be
  // restored (tap to show again) from the bell popup.
  const { dismiss, isDismissed } = useAnnouncementDismissal();
  // An announcement with a portal page (e.g. "New guide: …") gets an Open
  // button on its banner/corner card (#436). Opening it is seeing it: the card
  // is dismissed rather than left pinned over the page it just opened.
  // A guide link (`?guide=`) opens the guide modal over the current page.
  const guideModal = useGuideModal();
  const openPage = (a: (typeof announcements)[number], path: string) => {
    dismiss(a);
    const guideKey = guideKeyFromLink(path);
    if (guideKey && guideModal) guideModal.openGuide(guideKey);
    else navigate(path);
  };

  const visible = applyTakeoverSuppression(announcements).filter((a) => !isDismissed(a));

  // ``visible`` loses the audio typing through the shared helpers; map back to
  // the fetched rows (same objects) for the sound layer.
  const visibleIds = new Set(visible.map((a) => a.id));
  const audible = announcements.filter((a) => visibleIds.has(a.id));

  const groups = groupByDisplayType(visible);
  // Edge-to-edge takeover wins outright (and suppression has already hidden
  // the rest). Otherwise a regular fullscreen shows as a page-embedded modal.
  const takeover = groups.fullscreen_takeover[0];
  const modal = takeover ? undefined : groups.fullscreen[0];
  // Critical / emergency corner cards (the ones that can't be permanently
  // dismissed) keep their own always-visible slot; the rest share the popup.
  const urgentCorners = groups.corner.filter((a) => !isPermanentlyDismissable(a));
  const pagedCorners = groups.corner.filter((a) => isPermanentlyDismissable(a));

  return (
    <>
      <AnnouncementAudioPlayers announcements={audible} />
      <AnnouncementSoundPanel announcements={audible} />

      {groups.banner.map((a) => (
        <AnnouncementBanner key={a.id} announcement={a} onDismiss={() => dismiss(a)} onOpen={(path) => openPage(a, path)} />
      ))}

      {groups.corner.length > 0 && (
        <Box pos="fixed" style={{ left: 16, right: 16, bottom: 88, zIndex: 200, maxWidth: 360 }}>
          <Stack gap="sm">
            {urgentCorners.map((a) => (
              <AnnouncementCorner key={a.id} announcement={a} onDismiss={() => dismiss(a)} onOpen={(path) => openPage(a, path)} />
            ))}
            {pagedCorners.length > 0 && (
              <AnnouncementCornerCarousel announcements={pagedCorners} onDismiss={dismiss} onOpen={openPage} />
            )}
          </Stack>
        </Box>
      )}

      {takeover && (
        <AnnouncementFullscreen announcement={takeover} onClose={() => dismiss(takeover)} />
      )}

      {modal && <AnnouncementModal announcement={modal} onClose={() => dismiss(modal)} />}
    </>
  );
}
